<template>
  <div class="internal-payment">
    <organization-breadcrumb :items="[$t('internal_payment.title')]" />
    <div class="internal-payment__main">
      <el-row type="flex" justify="center">
        <el-col :xs="24" :md="14">
          <h1 class="blue-color" style="text-align: center">{{ $t("internal_payment.title") }}</h1>
          <el-form :rules="rules" :model="form" class="payment-form" ref="form" hide-required-asterisk>
            <el-row :gutter="20">
              <el-col :md="12" :xs="24">
                <div class="pick-shareable-to">
                  <elsa-select
                    name="product_type"
                    :label="$t('internal_payment.select_product_type')"
                    :options="products"
                    :default-first-option="true"
                    :placeholder="$t('internal_payment.select_product_type_placeholder')"
                    v-model.lazy="form.product_type"
                  />
                </div>
              </el-col>
              <el-col :md="12" :xs="24">
                <div class="pick-shareable-to">
                  <elsa-select
                    name="package"
                    :label="$t('internal_payment.select_package')"
                    :options="getPackageOptions"
                    :default-first-option="true"
                    :placeholder="$t('internal_payment.select_package_placeholder')"
                    :disabled="disabled"
                    v-model.lazy="form.package"
                  />
                </div>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :md="12" :xs="24">
                <elsa-input
                  v-model="form.quantity"
                  :label="$t('internal_payment.quantity')"
                  :placeholder="$t('internal_payment.quantity_placeholder')"
                  type="number"
                  name="quantity"
                />
              </el-col>
              <el-col :md="12" :xs="24">
                <elsa-input
                  v-model="form.price"
                  :label="$t('internal_payment.price')"
                  :placeholder="$t('internal_payment.price_placeholder')"
                  type="number"
                  name="price"
                />
              </el-col>
            </el-row>
            <el-row type="flex" justify="end">
              <primary-button
                type="submit"
                class="bigger"
                @click.native.prevent="handleSubscribe"
                v-loading.fullscreen.lock="loading"
              >
                {{ $t("plan_payment.payment_summary.subscribe") }}
              </primary-button>
            </el-row>
          </el-form>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import OrganizationBreadcrumb from "@/components/common/OrganizationBreadcrumb";
import ElsaSelect from "@/components/common/Select";
import ElsaInput from "@/components/common/Input";
import PrimaryButton from "@/components/common/buttons/PrimaryButton";
import { warningMessage, successMessage } from "@/utils/messages";
import { mapGetters } from "vuex";
import { DEFAULT_PRODUCT_TYPE, CODE_SITE, SUBSCRIPTION_PRODUCT_TYPES } from "@/constants/generic";

export default {
  data() {
    return {
      form: {
        package: "",
        quantity: undefined,
        price: undefined,
        product_type: DEFAULT_PRODUCT_TYPE,
      },
      rules: {
        package: [{ required: true, message: this.$t("internal_payment.select_package_error"), trigger: "change" }],
        quantity: [{ required: true, message: this.$t("internal_payment.quantity_error"), trigger: "blur" }],
        price: [{ required: true, message: this.$t("internal_payment.price_error"), trigger: "blur" }],
      },
      options: [
        {
          value: "one_year",
          label: "One year",
        },
        {
          value: "six_months",
          label: "Six months",
        },
        {
          value: "three_months",
          label: "Three months",
        },
        {
          value: "one_month",
          label: "One month",
        },
      ],
      premium_options: [
        {
          value: "lifetime",
          label: "Lifetime",
        },
      ],
      loading: false,
      disabled: false,
      prefixUriSite: '/v1/site/',
      products: [],
      defaultCountry: 'US',
      country: 'US',
    };
  },
  components: {
    OrganizationBreadcrumb,
    ElsaSelect,
    ElsaInput,
    PrimaryButton,
  },
  computed: {
    ...mapGetters({
      currentUserId: "user/getUserId",
      currentOrganizationId: "organization/currentOrganizationId",
    }),
    getPackageOptions() {
      if (SUBSCRIPTION_PRODUCT_TYPES.includes(this.form.product_type)) return this.options
      return this.premium_options
    }
  },
  methods: {
    handleSubscribe() {
      this.$refs.form.validate(async valid => {
        if (valid) {
          try {
            const payload = {
              package: this.form.package,
              quantity: parseInt(this.form.quantity),
              price: parseInt(this.form.price),
              user_id: this.currentUserId,
              organization_id: this.currentOrganizationId,
              saving: 0,
              currency: "VND",
              product_types: [this.form.product_type],
              product_content: this.form.product_content,
              product_content_ids: this.form.product_content_ids,
            };

            await this.$axios.post("/v2/checkouts/payment-internal", payload);
            this.$router.push(`/admin/organization/${this.currentOrganizationId}/plan`);
            return successMessage(this.$t("internal_payment.payment_success"))(this);
          } catch (error) {
            return warningMessage(this.$t("internal_payment.payment_error"))(this);
          }
        }
      });
    },
    getProductsFromCatalog() {
      // load products
      const lite_uri = this.prefixUriSite + CODE_SITE.LITE;
      const params = {
          'country': this.country,
        }
      const lite_request = this.$axios.get(process.env.VUE_APP_PRODUCT_CATALOG_URL + lite_uri , {params}).then((response) => {
        const data_lite = response.data["catalogs"];
        data_lite.forEach(item => {
          const products = item["products"];
          let product_content_ids = [];
          let product_content = products[0]["content"];
          products.forEach(product => {
            product_content_ids = [...product_content_ids, ...product["content_ids"]];
          });
          const label = item["name"];
          const value = item["code"];
          const ele = { label, value, product_content, product_content_ids};
          this.products.push(ele);
        })
      }).catch((error) => {
        throw error
      });
      lite_request;
    },
  },
  async mounted() {
    try {
      const ip = await this.$axios.get('https://elsaspeak.com/api/get-ip');
      this.country = ip.data ? ip.data['country'] : this.defaultCountry;
    } catch (error) {
      this.country = this.defaultCountry;
    }
    await this.getProductsFromCatalog();
  },
  watch: {
    "form.product_type"(newValue) {
      if (!SUBSCRIPTION_PRODUCT_TYPES.includes(newValue)) {
        this.form.package = this.premium_options[0].value
      } else {
        this.form.package = this.options[0].value
      }
      // get product content obj
      this.products.find((item) => {
        if (item.value === newValue) {
          this.form.product_content = item.product_content
          this.form.product_content_ids = item.product_content_ids
        }
      })
    },
  },
};
</script>

<style lang="scss" scoped>
.internal-payment {
  width: 100%;
  &__main {
    margin: 24px;
    h1 {
      font-size: 30px;
      line-height: 45px;
      font-weight: 700;
    }
    .payment-form {
      margin-top: 20px;
    }
  }
}
</style>
